/**
 * Capacitor Native Bootstrapping
 *
 * Bu modül React app'in native (iOS/Android) container'da çalışırken
 * ihtiyacı olan native davranışları kurar:
 *
 * 1. Deep link listener → `urunbulur://oauth/callback?...` yakalanır,
 *    Instagram OAuth callback'i içeride handle edilir.
 * 2. Status bar renk/style — light theme.
 * 3. Splash screen otomatik hide (2 sn sonra).
 * 4. Push notification token kayıt (WhatsApp/IG DM bildirimleri için).
 *
 * Bu dosya `index.js`'te bir kez import edilir; hiçbir React component
 * render etmez, sadece side-effect'lerini kurar.
 */

// Capacitor sadece native platformlarda mevcut; web'de bu import'lar
// silently fail eder. Çevresel kontrol yaparak yalnızca native'da çalıştırırız.
let Capacitor;
try {
  ({ Capacitor } = require('@capacitor/core'));
} catch (e) {
  // Web build'de node_modules yoksa (unlikely çünkü yarn install ediyor)
  Capacitor = null;
}

export function initCapacitor(navigate) {
  if (!Capacitor || !Capacitor.isNativePlatform || !Capacitor.isNativePlatform()) {
    return; // Web tarayıcıda hiçbir şey yapma
  }

  // html tag'e "capacitor-native" class ekle → CSS'te safe-area-inset padding'i uygula
  // (Web preview'da bu class olmadığı için normal görünür)
  document.documentElement.classList.add('capacitor-native');

  // 1) Deep link listener — urunbulur://oauth/callback?code=... yakalanır
  (async () => {
    try {
      const { App } = await import('@capacitor/app');
      App.addListener('appUrlOpen', (event) => {
        try {
          const url = new URL(event.url);
          // urunbulur://oauth/callback?code=... → app-internal /instagram/oauth/callback
          if (url.hostname === 'oauth' && url.pathname === '/callback') {
            const params = url.search || '';
            if (typeof navigate === 'function') {
              navigate(`/instagram/oauth/callback${params}`);
            } else {
              window.location.href = `/instagram/oauth/callback${params}`;
            }
          } else {
            // Fallback: pathname'e navigate et
            const path = url.pathname + url.search;
            if (typeof navigate === 'function') navigate(path);
            else window.location.href = path;
          }
        } catch (err) {
          console.warn('[capacitor] deep link parse hatası:', err, event);
        }
      });
    } catch (err) {
      console.warn('[capacitor] App plugin yüklenemedi:', err);
    }
  })();

  // 2) Status bar — mor tema (splash + brand rengiyle uyumlu)
  (async () => {
    try {
      const { StatusBar, Style } = await import('@capacitor/status-bar');
      // Light style = beyaz ikonlar/yazılar (mor arka planda okunabilir)
      await StatusBar.setStyle({ style: Style.Light });
      // Android'de bg rengi de set edilebilir. iOS'ta Info.plist'ten kontrol edilir.
      if (Capacitor.getPlatform() === 'android') {
        await StatusBar.setBackgroundColor({ color: '#4c1d95' });
      }
      // iOS'ta status bar overlay'i kapat — content status bar altına kaymasın
      await StatusBar.setOverlaysWebView({ overlay: false });
    } catch (err) {
      console.warn('[capacitor] StatusBar yüklenemedi:', err);
    }
  })();

  // 2b) Keyboard — iOS'ta klavye açıldığında body'yi yukarı it (input covering'ı önle)
  (async () => {
    try {
      const { Keyboard, KeyboardResize } = await import('@capacitor/keyboard');
      // iOS: klavye açılınca body/native içeriği yukarı it
      if (Capacitor.getPlatform() === 'ios') {
        await Keyboard.setResizeMode({ mode: KeyboardResize.Body });
        await Keyboard.setAccessoryBarVisible({ isVisible: false });
      }
      // Klavye event'lerini body class'ına yaz — CSS'te .kb-open ile stil verebilirsiniz
      Keyboard.addListener('keyboardWillShow', (info) => {
        document.body.classList.add('kb-open');
        document.body.style.setProperty('--kb-height', `${info.keyboardHeight}px`);
      });
      Keyboard.addListener('keyboardWillHide', () => {
        document.body.classList.remove('kb-open');
        document.body.style.removeProperty('--kb-height');
      });
    } catch (err) {
      console.warn('[capacitor] Keyboard yüklenemedi:', err);
    }
  })();

  // 3) Splash screen — konfig'de auto hide açık; ekstra güvence için 2.5 sn'de manuel hide
  (async () => {
    try {
      const { SplashScreen } = await import('@capacitor/splash-screen');
      setTimeout(() => SplashScreen.hide().catch(() => {}), 2500);
    } catch (err) {
      // Ignore
    }
  })();

  // 4) Push Notification kayıt
  //    - Kullanıcı token alındıktan sonra backend'e gönderilir → tenant_id ile
  //      eşleşecek şekilde `push_tokens` collection'ında saklanır.
  //    - WhatsApp/IG webhook mesaj geldiğinde bu token'a FCM/APNs push atılır.
  //    - ⚠️ NOT: Android'de Firebase (google-services.json) yoksa native crash olur.
  //      JS try/catch native process crash'ini yakalayamaz. Bu yüzden push init'i
  //      SADECE build sırasında REACT_APP_PUSH_ENABLED='true' set edilmişse çalıştırıyoruz.
  //      Bu flag GitHub Actions workflow'unda google-services.json bulunduğunda otomatik set edilir.
  const pushEnabled = process.env.REACT_APP_PUSH_ENABLED === 'true';
  if (!pushEnabled) {
    console.log('[push] Firebase config yok — push notification devre dışı (crash koruması)');
    return;
  }
  (async () => {
    try {
      const platform = Capacitor.getPlatform();
      const { PushNotifications } = await import('@capacitor/push-notifications');

      try {
        await PushNotifications.addListener('registrationError', (error) => {
          console.warn('[push] Registration error:', error);
        });
      } catch (_) { /* ignore */ }

      const permStatus = await PushNotifications.requestPermissions();
      if (permStatus.receive !== 'granted') return;

      try {
        await PushNotifications.register();
      } catch (regErr) {
        console.warn('[push] register() başarısız:', regErr);
        return;
      }

      PushNotifications.addListener('registration', async (token) => {
        console.log('[push] Registration token:', token.value.slice(0, 20) + '...');
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          if (!user?.username) return;
          const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
          await fetch(`${baseUrl}/api/push/register-device`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              username: user.username,
              companyName: user.companyName || user.company || null,
              platform,
              token: token.value,
            }),
          });
        } catch (err) {
          console.warn('[push] Backend kayıt hatası:', err);
        }
      });

      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('[push] Received:', notification);
      });

      PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        const data = action.notification?.data || {};
        if (data.route && typeof navigate === 'function') {
          navigate(data.route);
        } else if (data.route) {
          window.location.href = data.route;
        }
      });
    } catch (err) {
      console.warn('[capacitor] PushNotifications yüklenemedi:', err);
    }
  })();
}

/**
 * Native platform kontrolü (component'lerde koşullu render için)
 */
export function isNativePlatform() {
  try {
    return Capacitor && Capacitor.isNativePlatform && Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

export function getPlatform() {
  try {
    return Capacitor ? Capacitor.getPlatform() : 'web';
  } catch {
    return 'web';
  }
}
